import { faEllipsis, faLocationDot, faPencil, faTrashCan, faUsers } from '@fortawesome/pro-regular-svg-icons';
import { faLayerGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Avatar from 'components/Bookings2/List/Staff/Avatar';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { ListCard, ListCardRow } from 'components/UI/V3/ListCard';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { useTimeZone } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { OpsTask, OpsTaskStatusId } from 'model/OpsTask';
import { Staff } from 'model/Staff';
import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from 'store';
import OpsTaskStatusSelect, { OpsTaskStatusOption } from './OpsTaskStatusSelect';
import styles from './OpsTasksTableRow.module.scss';

interface OpsTasksTableRowProps {
    task: OpsTask;
    statusOptions: Array<OpsTaskStatusOption>;
    showLocationColumn?: boolean;
    className?: string;
    onEdit?: (task: OpsTask) => void;
    onDelete?: (task: OpsTask) => void;
    onChangeStatus?: (task: OpsTask, status: OpsTaskStatusId) => void;
}

export const OpsTasksTableRow: FunctionComponent<OpsTasksTableRowProps> = props => {
    const timeZone = useTimeZone();
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const { getOrganizationMarketplace, isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const staff = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const avatarFor = (id: number): string | null => staff.find(member => member.id === id)?.person.avatar ?? null;

    const allowActions = isUserLoggedInMarketplace(props.task.marketplaceId);
    const locationName = getOrganizationMarketplace(props.task.marketplaceId)?.name ?? '—';

    const date = props.task.dueAt.clone().tz(timeZone).format('MMM DD, YYYY');
    const time = props.task.dueAt.clone().tz(timeZone).format('h:mm a');

    const taskStaff = props.task.staff;
    const taskRooms = props.task.rooms;

    const renderStaff = () => {
        if (taskStaff.length === 0) {
            return <span className={styles.empty}>—</span>;
        }
        if (taskStaff.length === 1) {
            return (
                <div className={styles.chip}>
                    <Avatar
                        imageUrl={avatarFor(taskStaff[0].id)}
                        className={styles.chipAvatar}
                    />
                    <span className={styles.chipLabel}>{taskStaff[0].name}</span>
                </div>
            );
        }
        return (
            <div
                className={styles.chip}
                title={taskStaff.map(member => member.name).join(', ')}
            >
                <span className={styles.chipIcon}>
                    <FontAwesomeIcon icon={faUsers} />
                </span>
                <span className={styles.chipLabel}>{taskStaff.length} Staff</span>
            </div>
        );
    };

    const renderRooms = () => {
        if (taskRooms.length === 0) {
            return <span className={styles.empty}>—</span>;
        }
        const isSingle = taskRooms.length === 1;
        return (
            <div
                className={styles.chip}
                title={taskRooms.map(room => room.name).join(', ')}
            >
                <span className={styles.chipIcon}>
                    <FontAwesomeIcon icon={faLayerGroup} />
                </span>
                <span className={styles.chipLabel}>{isSingle ? taskRooms[0].name : `${taskRooms.length} Rooms`}</span>
            </div>
        );
    };

    const renderMobileStaff = () => {
        if (taskStaff.length === 0) {
            return <span className={styles.empty}>—</span>;
        }
        if (taskStaff.length === 1) {
            return (
                <div className={styles.mobileStaff}>
                    <Avatar
                        imageUrl={avatarFor(taskStaff[0].id)}
                        className={styles.mobileStaffAvatar}
                    />
                    <span className={styles.mobileStaffName}>{taskStaff[0].name}</span>
                </div>
            );
        }
        return (
            <div
                className={styles.mobileStaff}
                title={taskStaff.map(member => member.name).join(', ')}
            >
                <div className={styles.mobileStaffAvatars}>
                    {taskStaff.slice(0, 3).map(member => (
                        <Avatar
                            key={member.id}
                            imageUrl={avatarFor(member.id)}
                            className={styles.mobileStaffAvatar}
                        />
                    ))}
                </div>
                <span className={styles.mobileStaffName}>{taskStaff.length} Staff</span>
            </div>
        );
    };

    const actionsMenu = allowActions && (
        <Dropdown>
            <DropdownTrigger className={styles.actionsTrigger}>
                <FontAwesomeIcon
                    icon={faEllipsis}
                    className={styles.actionsIcon}
                />
            </DropdownTrigger>
            <DropdownContent
                minWidth={150}
                align="end"
                sideOffset={6}
            >
                <DropdownItem
                    leftIcon={faPencil}
                    onSelect={() => props.onEdit?.(props.task)}
                >
                    Edit
                </DropdownItem>
                <DropdownItem
                    leftIcon={faTrashCan}
                    onSelect={() => props.onDelete?.(props.task)}
                >
                    Delete
                </DropdownItem>
            </DropdownContent>
        </Dropdown>
    );

    const statusSelect = (fullWidth: boolean) => (
        <OpsTaskStatusSelect
            value={props.task.status}
            options={props.statusOptions}
            pastDue={props.task.pastDue}
            disabled={!allowActions}
            fullWidth={fullWidth}
            onChange={status => props.onChangeStatus?.(props.task, status)}
        />
    );

    if (isMobile) {
        return (
            <TableRow className={clsx(styles.mobileRow, props.className)}>
                <TableCell className={styles.mobileCell}>
                    <ListCard>
                        <ListCardRow>
                            {taskRooms.length > 0 ? (
                                <span
                                    className={styles.mobileRooms}
                                    title={taskRooms.map(room => room.name).join(', ')}
                                >
                                    {taskRooms.length === 1 ? taskRooms[0].name : `${taskRooms.length} Rooms`}
                                </span>
                            ) : (
                                <span className={styles.empty}>—</span>
                            )}
                            {props.showLocationColumn && (
                                <span className={styles.location}>
                                    <FontAwesomeIcon
                                        icon={faLocationDot}
                                        className={styles.locationIcon}
                                    />
                                    {locationName}
                                </span>
                            )}
                        </ListCardRow>

                        <ListCardRow>
                            {renderMobileStaff()}
                            <div className={styles.mobileDate}>
                                <span className={styles.date}>{date}</span>
                                <span className={styles.time}>{time}</span>
                            </div>
                        </ListCardRow>

                        <ListCardRow className={styles.mobileDescriptionRow}>
                            <span className={styles.mobileDescription}>{props.task.description}</span>
                            {actionsMenu}
                        </ListCardRow>

                        <ListCardRow className={styles.mobileStatusRow}>{statusSelect(true)}</ListCardRow>
                    </ListCard>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={clsx(styles.row, props.className)}>
            <TableCell className={styles.dateCell}>
                <div className={styles.dateStack}>
                    <span className={styles.date}>{date}</span>
                    <span className={styles.time}>{time}</span>
                    {props.showLocationColumn && (
                        <span className={styles.location}>
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                className={styles.locationIcon}
                            />
                            {locationName}
                        </span>
                    )}
                </div>
            </TableCell>

            <TableCell className={styles.taskCell}>{props.task.description}</TableCell>

            <TableCell className={styles.staffCell}>{renderStaff()}</TableCell>

            <TableCell className={styles.roomCell}>{renderRooms()}</TableCell>

            <TableCell className={styles.statusCell}>{statusSelect(false)}</TableCell>

            <TableCell className={styles.actionsCell}>{actionsMenu}</TableCell>
        </TableRow>
    );
};

export default OpsTasksTableRow;
